import { ReactNode, useEffect, useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Chip,
    Divider,
    Grid,
    Paper,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Button,
    useTheme,
} from '@mui/material';

import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { fetchBaseQueryWithEncryption } from '@/features/fetchBaseQueryWithEncryption';
import SessionRunTab from './SessionRunTab';

type UISessionTabProps = {
    testScenario: any;
};

const UISessionTab = ({ testScenario }: UISessionTabProps) => {
    const theme = useTheme();
    // const [suiteDetails, setSuiteDetails] = useState<any>(null);
    const [scenarioDetails, setScenarioDetails] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'details' | 'run'>('details');

    const apiBaseUrl = useMemo(() => import.meta.env.VITE_COLLECTION_API_URL, []);
    const scenarioUrl = useMemo(() => {
        if (!testScenario) {
            return '';
        }

        const baseUrl = import.meta.env.VITE_WEB_URL;
        if (!baseUrl) {
            return '';
        }

        return `${baseUrl}/api-lab/ui-automation-testing/scenario/${testScenario}`;
    }, [testScenario]);
    const parsedTestCases = useMemo(() => {
        if (!scenarioDetails?.testCases) {
            return null;
        }

        try {
            if (typeof scenarioDetails.testCases === 'string') {
                return JSON.parse(scenarioDetails.testCases);
            }

            return scenarioDetails.testCases;
        } catch {
            return null;
        }
    }, [scenarioDetails?.testCases]);

    const testCaseSummary = useMemo(() => {
        if (!parsedTestCases?.data || typeof parsedTestCases.data !== 'object') {
            return null;
        }

        const entries = Object.values(parsedTestCases.data);
        let totalSteps = 0;

        entries.forEach((value) => {
            if (Array.isArray(value)) {
                totalSteps += value.length;
            }
        });

        return {
            scenarios: Object.keys(parsedTestCases.data).length,
            steps: totalSteps,
        };
    }, [parsedTestCases]);

    const stepRows = useMemo(() => {
        if (!parsedTestCases?.data || typeof parsedTestCases.data !== 'object') {
            return [];
        }

        const rows: Array<{
            scenario: string;
            index: number;
            type?: string;
            element?: string;
            value?: string;
            url?: string;
            path?: string;
            waitFor?: number;
            timestamp?: number;
        }> = [];

        Object.entries(parsedTestCases.data).forEach(([scenarioName, steps]) => {
            if (!Array.isArray(steps)) {
                return;
            }

            steps.forEach((step, idx) => {
                rows.push({
                    scenario: scenarioName,
                    index: idx + 1,
                    type: step?.type,
                    element: step?.element,
                    value: step?.value,
                    url: step?.url,
                    path: step?.path,
                    waitFor: step?.waitFor,
                    timestamp: step?.timestamp,
                });
            });
        });

        return rows;
    }, [parsedTestCases]);

    const formatDate = (value?: string | null) => {
        if (!value) {
            return '—';
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return value;
        }

        return parsed.toLocaleString();
    };

    const formatTimestamp = (value?: number) => {
        if (!value) {
            return '—';
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return String(value);
        }

        return parsed.toLocaleTimeString();
    };

    useEffect(() => {
        let isActive = true;

        const fetchDetails = async (folderId: string) => {
            const response = await fetchBaseQueryWithEncryption(
                `${apiBaseUrl}/folder/${folderId}?withDetails=true`,
                {
                    method: 'GET',
                    headers: {
                        'accessToken': localStorage.getItem('accessToken') || '',
                    },
                }
            );

            const responseData = response?.data as any;
            return responseData?.decryptedData ?? responseData?.localData ?? responseData;
        };

        const loadDetails = async () => {
            if (!apiBaseUrl || !testScenario) {
                return;
            }

            setIsLoading(true);
            setErrorMessage(null);

            try {
                const [scenarioData] = await Promise.all([
                    fetchDetails(String(testScenario)),
                    // fetchDetails(String(testSuite)),
                ]);

                if (!isActive) {
                    return;
                }

                setScenarioDetails(scenarioData);
                // setSuiteDetails(suiteData);
            } catch (error) {
                if (!isActive) {
                    return;
                }

                setErrorMessage(
                    error instanceof Error ? error.message : 'Failed to load UI session data.'
                );
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        };

        loadDetails();

        return () => {
            isActive = false;
        };
    }, [apiBaseUrl, testScenario]);

    return (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
                sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: `0 1px 3px ${theme.palette.action.focus}`,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 3,
                        py: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.action.hover,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <PlayCircleOutlineIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                                UI Session
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Manage and run UI automation test scenarios
                            </Typography>
                        </Box>
                    </Stack>
                    <Button
                        variant="outlined"
                        size="small"
                        component="a"
                        href={scenarioUrl || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={!scenarioUrl}
                        sx={{
                            textTransform: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            px: 2,
                        }}
                    >
                        Manage Scenario
                    </Button>
                </Box>

                {/* Body */}
                <Box sx={{ p: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, value) => setActiveTab(value)}
                        textColor="primary"
                        indicatorColor="primary"
                        sx={{
                            minHeight: 36,
                            mb: 2.5,
                            '& .MuiTab-root': {
                                minHeight: 36,
                                py: 0.5,
                                px: 2,
                                fontSize: '13px',
                                fontWeight: 600,
                                textTransform: 'none',
                            },
                        }}
                    >
                        <Tab label="Details" value="details" />
                        <Tab label="Run" value="run" />
                    </Tabs>
            {activeTab === 'details' ? (
                <>
                    {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
                    {isLoading ? (
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                backgroundColor: theme.palette.background.paper,
                                p: 2.5,
                            }}
                        >
                            <Stack spacing={1}>
                                <Skeleton variant="text" width="40%" />
                                <Skeleton variant="text" width="80%" />
                                <Skeleton variant="text" width="30%" />
                            </Stack>
                        </Box>
                    ) : null}
                    {!isLoading && !scenarioDetails ? (
                        <Paper
                            elevation={0}
                            sx={{
                                py: 5,
                                px: 3,
                                textAlign: 'center',
                                borderRadius: '14px',
                                border: `1.5px dashed ${theme.palette.divider}`,
                                background:
                                    theme.palette.mode === 'dark'
                                        ? `linear-gradient(135deg, ${theme.palette.primary.main}0A 0%, ${theme.palette.primary.main}06 100%)`
                                        : `linear-gradient(135deg, ${theme.palette.primary.main}06 0%, ${theme.palette.primary.main}04 100%)`,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '14px',
                                    mx: 'auto',
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor:
                                        theme.palette.mode === 'dark'
                                            ? `${theme.palette.primary.main}20`
                                            : `${theme.palette.primary.main}14`,
                                }}
                            >
                                <InfoOutlinedIcon
                                    sx={{
                                        fontSize: 28,
                                        color: 'primary.main',
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="body1"
                                fontWeight={800}
                                fontSize="14px"
                                color="text.primary"
                                sx={{ mb: 0.5 }}
                            >
                                Select a test scenario to see details.
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                fontSize="12px"
                                sx={{ display: 'block', maxWidth: 380, mx: 'auto', lineHeight: 1.7 }}
                            >
                                Choose a test scenario from your project to view its configuration and run details.
                            </Typography>
                        </Paper>
                    ) : null}
                    {!isLoading && scenarioDetails ? (
                        <>
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: theme.palette.background.paper,
                                    p: 2.5,
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {scenarioDetails?.itemName || 'Test Scenario'}
                                    </Typography>
                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                        <Chip
                                            label={scenarioDetails?.type || 'Scenario'}
                                            size="small"
                                            variant="outlined"
                                        />
                                        {scenarioDetails?.scenarioType ? (
                                            <Chip
                                                label={scenarioDetails.scenarioType.replace('_', ' ')}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ) : null}
                                        {scenarioDetails?.isPublic !== undefined ? (
                                            <Chip
                                                label={scenarioDetails.isPublic ? 'Public' : 'Private'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ) : null}
                                        {scenarioDetails?.isGenerating ? (
                                            <Chip label="Generating" size="small" color="warning" />
                                        ) : null}
                                    </Stack>
                                    {scenarioDetails?.descriptions ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {scenarioDetails.descriptions}
                                        </Typography>
                                    ) : null}
                                </Stack>
                            </Box>

                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: theme.palette.background.paper,
                                    p: 2.5,
                                }}
                            >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Overview
                                    </Typography>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Stack spacing={1}>
                                                <InfoRow
                                                    label="Created by"
                                                    value={scenarioDetails?.createdBy?.fullName}
                                                />
                                                <InfoRow
                                                    label="Created at"
                                                    value={formatDate(scenarioDetails?.createdAt)}
                                                />
                                                <InfoRow
                                                    label="Updated at"
                                                    value={formatDate(scenarioDetails?.updatedAt)}
                                                />
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Stack spacing={1}>
                                                <InfoRow
                                                    label="Team"
                                                    value={scenarioDetails?.team?.teamName}
                                                />
                                                <InfoRow
                                                    label="Project"
                                                    value={scenarioDetails?.project?.title}
                                                />
                                                <InfoRow
                                                    label="Collection"
                                                    value={scenarioDetails?.apiCollection?.itemName}
                                                />
                                                <InfoRow
                                                    label="Expiry"
                                                    value={formatDate(scenarioDetails?.expiryDate)}
                                                />
                                                <InfoRow
                                                    label="Sync status"
                                                    value={
                                                        scenarioDetails?.isSync
                                                            ? 'Synced'
                                                            : 'Not synced'
                                                    }
                                                />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                            </Box>

                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: theme.palette.background.paper,
                                    p: 2.5,
                                }}
                            >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Test Steps
                                    </Typography>
                                    <Divider sx={{ my: 1.5 }} />
                                    {testCaseSummary ? (
                                        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                            <Chip
                                                label={`${testCaseSummary.scenarios} scenarios`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                            <Chip
                                                label={`${testCaseSummary.steps} steps`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No recorded steps found.
                                        </Typography>
                                    )}
                                    {stepRows.length ? (
                                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>#</TableCell>
                                                        <TableCell>Scenario</TableCell>
                                                        <TableCell>Action</TableCell>
                                                        <TableCell>Element</TableCell>
                                                        <TableCell>Value</TableCell>
                                                        <TableCell>Path</TableCell>
                                                        <TableCell>URL</TableCell>
                                                        <TableCell>Wait (ms)</TableCell>
                                                        <TableCell>Time</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {stepRows.map((row, idx) => (
                                                        <TableRow
                                                            key={`${row.scenario}-${row.index}-${idx}`}
                                                        >
                                                            <TableCell>{row.index}</TableCell>
                                                            <TableCell>{row.scenario}</TableCell>
                                                            <TableCell>{row.type ?? '—'}</TableCell>
                                                            <TableCell>{row.element ?? '—'}</TableCell>
                                                            <TableCell>{row.value ?? '—'}</TableCell>
                                                            <TableCell>{row.path ?? '—'}</TableCell>
                                                            <TableCell>{row.url ?? '—'}</TableCell>
                                                            <TableCell>{row.waitFor ?? '—'}</TableCell>
                                                            <TableCell>
                                                                {formatTimestamp(row.timestamp)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : null}
                                    {parsedTestCases ? (
                                        <Accordion
                                            sx={{
                                                mt: 1.5,
                                                borderRadius: '8px !important',
                                                '&:before': { display: 'none' },
                                                boxShadow: 'none',
                                                border: `1px solid ${theme.palette.divider}`,
                                            }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography variant="body2">
                                                    View raw steps
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Box
                                                    component="pre"
                                                    sx={{
                                                        m: 0,
                                                        p: 2,
                                                        bgcolor: 'grey.100',
                                                        borderRadius: 1,
                                                        maxHeight: 320,
                                                        overflow: 'auto',
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {JSON.stringify(parsedTestCases, null, 2)}
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    ) : null}
                            </Box>
                        </>
                    ) : null}
                </>
            ) : (
                <SessionRunTab scenarioId={testScenario} scenarioDetails={scenarioDetails} />
            )}
                </Box>
            </Box>
        </Box>
    );
};

export default UISessionTab;

type InfoRowProps = {
    label: string;
    value?: ReactNode;
};

const InfoRow = ({ label, value }: InfoRowProps) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value ?? '—'}
        </Typography>
    </Box>
);